import React, { useState } from "react";
import { Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { RiDeleteBinLine } from "react-icons/ri";
import useGetAttributes from "../../../hooks/attributes/useGetAttributes";
import useDeleteAttribute from "../../../hooks/attributes/useDeleteAttribute";
import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import Pagination from "../../../components/elements/pagination";
import DeleteModal from "../../../components/elements/deleteModal";
import EditAttribute from "./EditAttribute";
import toast from "react-hot-toast";

const AttributesList = () => {
  const {
    data,
    handlePageClick,
    handleSearchVal,
    paramsObject,
    handleSetLimit,
  } = useGetAttributes();

  const {
    attributeId,
    setAttributeId,
    isDeleteModalOpen,
    setDeleteModalOpen,
    handleSubmit,
  } = useDeleteAttribute();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const handleDelete = (id) => {
    setAttributeId(id);
    setDeleteModalOpen(true);
  };

  const handleEdit = (attributeData) => {
    setEditData(attributeData);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditData(null);
  };

  return (
    <div className="category px-4">
      <div className="category__head headingBorder__b">
        <h2 className="titleHeading">Attribute Values</h2>
      </div>
      <div className="category_table py-5 space-y-5">
        {/* Controls Row */}
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="min-w-[21rem] md:w-auto md:flex-1">
            <input
              onChange={handleSearchVal}
              value={paramsObject.searchQuery}
              type="text"
              placeholder="Search by name..."
              className="input"
            />
          </div>
          <div>
            <label htmlFor="" className="label">
              Limit
            </label>
            <select
              name="limit"
              value={data.data?.per_page || paramsObject.limit}
              className="input"
              onChange={handleSetLimit}
            >
              {Array(5)
                .fill(1)
                .map((_, idx) => {
                  return (
                    <option key={idx} value={(idx + 1) * 10}>
                      {(idx + 1) * 10}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {/* Table */}
        {data.isLoading || data.isFetching ? (
          <Spinner />
        ) : data.status === "error" ? (
          <Errors errorObject={data.error} inline />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
            <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
              <thead>
                <tr className="text-left">
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-16">Sr No</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Image
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-44">Attribute Name</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-44">Attribute Value</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.data?.data?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-gray-500 font-medium"
                    >
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  data.data?.data?.map((attributeData, idx) => {
                    const {
                      attribute_value_id,
                      attribute_name,
                      attribute_value,
                      icon_url,
                    } = attributeData || {};
                    return (
                      <tr key={attribute_value_id || idx}>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {paramsObject.limit * paramsObject.pageNo + idx + 1}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          {icon_url ? (
                            <div className="flex items-center justify-center">
                              <img
                                src={icon_url}
                                alt={attribute_name}
                                style={{
                                  maxHeight: "48px",
                                  maxWidth: "80px",
                                  objectFit: "contain",
                                  width: "auto",
                                  height: "auto",
                                  display: "block",
                                }}
                                className="rounded bg-white"
                              />
                            </div>
                          ) : (
                            <div className="h-12 w-12 mx-auto flex items-center justify-center bg-gray-200 text-gray-500 text-xs font-medium rounded">
                              No Image
                            </div>
                          )}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {attribute_name}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {attribute_value}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          <div className="flex items-center justify-center">
                            <IconButton
                              onClick={() => handleEdit(attributeData)}
                            >
                              <EditIcon />
                            </IconButton>
                            <div
                              onClick={() =>
                                handleDelete(attribute_value_id)
                              }
                            >
                              <IconButton>
                                <RiDeleteBinLine className="text-lg text-gray-600" />
                              </IconButton>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <Pagination
            currentPage={+paramsObject.pageNo}
            lengthofItems={data.data?.total_count}
            limit={paramsObject.limit}
            onPageChange={handlePageClick}
            pageCount={Math.ceil(
              (data.data?.total_count || 0) / (data.data?.per_page || 10)
            )}
          />
        </div>
      </div>

      <EditAttribute
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        editData={editData}
      />

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title={"Delete Attribute"}
        message={"Are you sure you want to delete this attribute?"}
        onDelete={() =>
          handleSubmit({ attribute_value_id: attributeId })
        }
      />
    </div>
  );
};

export default AttributesList;